import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../@/components/ui/dialog";
import { Button } from "../../@/components/ui/button";
import { Input } from "../../@/components/ui/input";
import { UploadCloud, FileIcon, Trash2 } from "lucide-react";
import { api_endpoint } from "@/constants/constant";
import axios from "axios";
import { useAuth } from "@clerk/clerk-react";
import { toast } from "sonner";
import { ClipLoader } from "react-spinners";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { fetchAllPaginatedItems } from "@/lib/fetchAllPaginatedItems";

const SUPPORTED_EXTENSIONS = [".csv", ".xlsx", ".xls"];

const parseSpreadsheet = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const data = new Uint8Array(reader.result);
      const wb = XLSX.read(data, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      resolve(XLSX.utils.sheet_to_json(ws));
    };
    reader.readAsArrayBuffer(file);
  });

const parseCsvFile = (file) =>
  new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => resolve(results.data),
      error: reject,
    });
  });

const parseFile = (file) =>
  file.name.toLowerCase().endsWith(".csv") ? parseCsvFile(file) : parseSpreadsheet(file);

const normaliseEmail = (row = {}) =>
  (row.email ?? row.Email ?? row.emails ?? row.Emails ?? row["e-mail"] ?? "")
    .toString()
    .trim()
    .toLowerCase();

export default function CourseBulkEnrollDialog({
  courseId,
  onUploaded,
  open,
  onOpenChange,
  enrolledStudentIds = [],
  templateUrl = `https://lms-internal.zinduaschool.com/templates/bulk-enroll-emails.csv`,
}) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [allStudents, setAllStudents] = useState([]);
  const fileInputRef = useRef(null);
  const { getToken } = useAuth();


  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const token = await getToken();
          const users = await fetchAllPaginatedItems({
            url: `${api_endpoint}/user/users`,
            token,
            selectItems: (data) => data?.users ?? [],
            mode: "skip",
            pageSize: 200,
            headers: { Authorization: `Bearer ${token}` },
          });
          setAllStudents(users ?? []);
      } catch (error) {
        console.error("Failed to fetch users", error);
        setAllStudents([]);
      }
    })();
  }, [open, getToken]);

  const addFiles = (incomingList) => {
    if (!incomingList?.length) return;
    setFiles((prev) => {
      const seen = new Set(prev.map((file) => `${file.name}-${file.size}`));
      const next = Array.from(incomingList).filter((file) => {
        const extension = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
        if (!SUPPORTED_EXTENSIONS.includes(extension)) {
          toast.error(`Unsupported file type: ${extension}. Please upload CSV files.`);
          return false;
        }
        const key = `${file.name}-${file.size}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      return [...prev, ...next];
    });
  };

  const onFileChange = (event) => addFiles(event.target.files);
  const onDrop = (event) => {
    event.preventDefault();
    addFiles(event.dataTransfer.files);
  };

  const removeFile = (name, size) =>
    setFiles((prev) => prev.filter((file) => file.name !== name || file.size !== size));

  const handleUpload = async () => {
    if (!files.length || !courseId) return;
    setUploading(true);
    try {
      const token = await getToken();
      const parsedRows = (await Promise.all(files.map((file) => parseFile(file)))).flat();
      const emails = parsedRows
        .map(normaliseEmail)
        .filter(Boolean);

      if (!emails.length) {
        toast.error("No valid emails detected in the uploaded files.");
        setUploading(false);
        return;
      }

      const emailToId = new Map(
        allStudents.map((student) => [student.email?.trim().toLowerCase(), student.id])
      );

      const matchedIds = [];
      const missingEmails = [];
      emails.forEach((email) => {
        const id = emailToId.get(email);
        if (id) {
          matchedIds.push(id);
        } else {
          missingEmails.push(email);
        }
      });

      if (!matchedIds.length) {
        toast.error("No matching users found for the uploaded emails.");
        setUploading(false);
        return;
      }

      const uniqueMatchedIds = Array.from(new Set(matchedIds));
      const alreadyEnrolledSet = new Set(enrolledStudentIds || []);
      const alreadyEnrolledIds = uniqueMatchedIds.filter((id) => alreadyEnrolledSet.has(id));
      const newEnrollmentIds = uniqueMatchedIds.filter((id) => !alreadyEnrolledSet.has(id));
      const missingCount = new Set(missingEmails).size;

      if (!newEnrollmentIds.length) {
        toast.info(
          `${alreadyEnrolledIds.length} already enrolled. ${missingCount} emails not found. No new students added.`
        );
        setUploading(false);
        return;
      }

      await axios.post(
        `${api_endpoint}/course/enroll/batch`,
        {
          course_id: courseId,
          user_ids: newEnrollmentIds,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      toast.success(
        `${newEnrollmentIds.length} students enrolled. ${alreadyEnrolledIds.length} already enrolled. ${missingCount} emails not found.`
      );
      setFiles([]);
      onOpenChange?.(false);
      onUploaded?.();
    } catch (error) {
      console.error("Bulk enrolment failed", error);
      toast.error(error?.response?.data?.message ?? "Failed to upload enrolments. Try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        onOpenChange(value);
        if (!value) {
          setFiles([]);
          setUploading(false);
        }
      }}
    >
      <DialogContent className="max-w-lg w-[95vw] bg-(--surface-base) text-(--neutral-900)">
        <DialogHeader>
          <DialogTitle className="text-2xl font-heading">Bulk Enrol Students</DialogTitle>
          <DialogDescription className="text-sm text-(--neutral-600)">
            Upload CSV file of students to enroll.{" "}
            <a
              href={templateUrl}
              download
              target="_blank"
              rel="noopener noreferrer"
              className="text-(--color-primary) underline"
            >
              Click to download template
            </a>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div
            onDragOver={(event) => event.preventDefault()}
            onDrop={onDrop}
            className="flex flex-col items-center justify-center gap-3 border border-dashed border-(--neutral-300) rounded-lg py-10 px-4 text-center bg-(--surface-base) text-(--neutral-600)"
          >
            <FileIcon className="w-10 h-10 text-(--neutral-400)" />
            <p className="font-heading text-(--neutral-800)">
              Drag and drop or
              {" "}
              <button
                type="button"
                className="text-(--color-primary) underline ml-1"
                onClick={() => fileInputRef.current?.click()}
              >
                Choose file
              </button>
              {" "}
              to upload
            </p>
            <Input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xls,.xlsx"
              className="hidden"
              multiple
              onChange={onFileChange}
            />
          </div>
          <div className="flex justify-between">
            <p className="text-xs text-(--neutral-500)">
              Accepted file types: CSV, XLS, XLSX
            </p>
            <p className="text-xs text-(--neutral-500)">
              Max file size: 10MB
            </p>
          </div>

          {files.length > 0 && (
            <div className="space-y-2 max-h-40 overflow-y-auto bg-(--neutral-50)">
              {files.map((file) => (
                <div
                  key={`${file.name}-${file.size}`}
                  className="flex items-center justify-between border border-(--neutral-200) rounded-md px-3 py-2 bg-(--neutral-100)"
                >
                  <div className="flex items-center gap-3">
                    <FileIcon className="w-5 h-5 text-(--color-primary)" />
                    <div>
                      <p className="text-sm font-medium">{file.name}</p>
                      <p className="text-xs text-(--neutral-500)">
                        {(file.size / (1024 * 1024)).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="text-(--neutral-500) hover:text-(--color-destructive)"
                    onClick={() => removeFile(file.name, file.size)}
                    aria-label="Remove file"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false);
              setFiles([]);
            }}
            disabled={uploading}
          >
            Cancel
          </Button>
          <Button type="button" className="" onClick={handleUpload} disabled={!files.length || uploading}>
            {uploading ? (
              <span className="flex items-center gap-2">
                <ClipLoader size={18} color="#fff" />
              </span>
            ) : (
              "Confirm Upload"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
